import React, { useRef, useState, useMemo } from 'react';
import { typography, semantic, radius } from '@/shared/theme/tokens';
import { lastNDaysRange } from '@/shared/lib/dateRange';
import { fmt2 } from '@/shared/lib/formatters';
import { SlideModal } from '@/shared/ui/SlideModal';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Tooltip as ChartTooltip, Filler, Legend,
} from 'chart.js';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import ZoomPlugin from 'chartjs-plugin-zoom';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useParameterCharts } from '../hooks/useDeploymentSettings';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ChartTooltip, Filler, Legend, ZoomPlugin,
);

// ── Constants ─────────────────────────────────────────────────────────────────
const DATE_RANGES = [
  { key: '1d',  label: '1D',  days: 1  },
  { key: '7d',  label: '7D',  days: 7  },
  { key: '14d', label: '14D', days: 14 },
  { key: '30d', label: '30D', days: 30 },
];

const CHART_H_WIDE = 280;
const CHART_H      = 260;

// Box drag-zoom only — wheel/pinch are glitchy
const ZOOM_CFG = {
  pan: { enabled: false },
  zoom: {
    wheel: { enabled: false },
    pinch: { enabled: false },
    mode: 'xy',
    drag: {
      enabled: true,
      backgroundColor: 'rgba(30,67,150,0.07)',
      borderColor: 'rgba(30,67,150,0.25)',
      borderWidth: 1,
    },
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function rangeToISO(days) {
  return lastNDaysRange(days);
}

function fmtLabel(iso) {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function Empty({ c, h }) {
  return (
    <div style={{ height: h, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: typography.fontSize.md, color: c.textSubtle }}>
      No data
    </div>
  );
}

function BoxPlotEmpty({ c, h }) {
  return (
    <div style={{
      height: h, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 10,
    }}>
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none" style={{ opacity: 0.35 }}>
        <rect x="14" y="16" width="24" height="20" rx="2" stroke={c.textSubtle} strokeWidth="1.8" fill="none" />
        <line x1="14" y1="26" x2="38" y2="26" stroke={c.textSubtle} strokeWidth="1.8" />
        <line x1="26" y1="8" x2="26" y2="16" stroke={c.textSubtle} strokeWidth="1.8" strokeDasharray="3 2" />
        <line x1="26" y1="36" x2="26" y2="44" stroke={c.textSubtle} strokeWidth="1.8" strokeDasharray="3 2" />
        <line x1="20" y1="8" x2="32" y2="8" stroke={c.textSubtle} strokeWidth="1.8" />
        <line x1="20" y1="44" x2="32" y2="44" stroke={c.textSubtle} strokeWidth="1.8" />
        <line x1="8" y1="46" x2="44" y2="46" stroke={c.textSubtle} strokeWidth="1.2" strokeOpacity="0.5" />
      </svg>
      <div style={{ textAlign: 'center', lineHeight: 1.5 }}>
        <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, marginBottom: 3 }}>
          Box plot not applicable
        </div>
        <div style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, maxWidth: 220 }}>
          Data granularity is too low to compute distributions. Select a finer time range or a parameter with more readings.
        </div>
      </div>
    </div>
  );
}

function ResetBtn({ chartRef, c }) {
  return (
    <button
      onClick={() => chartRef.current?.resetZoom?.()}
      style={{
        position: 'absolute', top: 4, right: 4, zIndex: 2,
        padding: '2px 7px', borderRadius: radius.sm, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold,
        border: `1px solid ${c.border}`, background: c.bg, color: c.textSubtle,
        cursor: 'pointer', fontFamily: typography.fontFamily.body, opacity: 0.85,
      }}
    >Reset</button>
  );
}

function SectionTitle({ label, c }) {
  return (
    <p style={{ margin: '0 0 8px', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
      {label}
    </p>
  );
}

function legendCfg(c) {
  return {
    display: true, position: 'bottom',
    labels: { color: c.textSubtle, font: { size: 10 }, boxWidth: 10, padding: 8, usePointStyle: true },
  };
}

function scaleCfg(c, pointCount = 2) {
  const single = pointCount <= 1;
  return {
    x: {
      // offset+autoSkip:false centers and labels the lone point when there is only one datum (AUT-131)
      offset: single,
      ticks: { color: c.textSubtle, font: { size: 9 }, maxRotation: 45, autoSkip: !single, maxTicksLimit: 12 },
      grid:  { color: c.border },
    },
    y: {
      ticks: { color: c.textSubtle, font: { size: 9 } },
      grid:  { color: c.border },
    },
  };
}

// ── Run Chart ─────────────────────────────────────────────────────────────────
function RunChart({ data, c, stretch }) {
  const ref = useRef(null);
  if (!data?.points?.length) return <Empty c={c} h={stretch ? '100%' : CHART_H_WIDE} />;

  const { points, timestamps, optimalMin, optimalMax } = data;
  const labels = timestamps ? timestamps.map(fmtLabel) : points.map((_, i) => `T${i}`);

  const ptColors = points.map((v) => {
    const ok = (optimalMin == null || v >= optimalMin) && (optimalMax == null || v <= optimalMax);
    return ok ? 'rgba(22,163,74,0.9)' : 'rgba(220,38,38,0.9)';
  });

  const datasets = [
    {
      label: 'Value', data: points,
      borderColor: 'rgba(30,67,150,0.65)', borderWidth: 1.5,
      pointRadius: 2.5, pointBackgroundColor: ptColors, pointBorderColor: ptColors,
      tension: 0.3, fill: false,
    },
  ];
  if (optimalMin != null) datasets.push({
    label: 'Opt Min', data: Array(points.length).fill(optimalMin),
    borderColor: 'rgba(22,163,74,0.55)', borderWidth: 1.5, borderDash: [5, 4],
    pointRadius: 0, fill: false, tension: 0,
  });
  if (optimalMax != null) datasets.push({
    label: 'Opt Max', data: Array(points.length).fill(optimalMax),
    borderColor: 'rgba(22,163,74,0.55)', borderWidth: 1.5, borderDash: [5, 4],
    pointRadius: 0, fill: optimalMin != null ? '-1' : false,
    backgroundColor: 'rgba(22,163,74,0.06)', tension: 0,
  });

  const sc = scaleCfg(c, points.length);
  const opts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    layout: { padding: { left: 12, right: 12 } },
    plugins: {
      legend: legendCfg(c),
      tooltip: {
        mode: 'nearest', intersect: false,
        filter: (item) => item.datasetIndex === 0,
        callbacks: {
          title: (items) => {
            if (!timestamps?.length || !items.length) return '';
            return new Date(timestamps[items[0].dataIndex]).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            });
          },
          label: (ctx) => ` ${fmt2(ctx.parsed.y)}`,
        },
      },
      zoom: ZOOM_CFG,
    },
    scales: sc,
  };

  return (
    <div style={{ height: stretch ? '100%' : CHART_H_WIDE, position: 'relative' }}>
      <Line ref={ref} data={{ labels, datasets }} options={opts} />
      <ResetBtn chartRef={ref} c={c} />
    </div>
  );
}

// ── Box Plot ──────────────────────────────────────────────────────────────────
// Ported from jspl_digital_twin reference:
// - [q1, median] blue floating bar
// - [median, q3] green floating bar
// - thin [min, q1] and [q3, max] slate whisker bars (barPercentage 0.03)
// - outliers as scatter dataset (amber dots)
// - opt min/max as line datasets
function BoxPlotChart({ data, c, stretch }) {
  const ref        = useRef(null);
  if (!data?.boxes?.length) return <BoxPlotEmpty c={c} h={stretch ? '100%' : CHART_H_WIDE} />;
  const { boxes, optimalMin, optimalMax } = data;

  const labels   = boxes.map((b) => b.date);
  const blueBox  = boxes.map((b) => [b.q1,    b.median]);
  const greenBox = boxes.map((b) => [b.median, b.q3]);
  const whiskLo  = boxes.map((b) => [b.lowerWhisker, b.q1]);
  const whiskHi  = boxes.map((b) => [b.q3, b.upperWhisker]);

  const outliersData = boxes.flatMap((b, i) =>
    (b.outliers ?? []).map((v) => ({ x: labels[i], y: v }))
  );

  const allVals = boxes.flatMap((b) => [b.lowerWhisker, b.upperWhisker]).filter((v) => v != null);
  if (optimalMin != null) allVals.push(optimalMin);
  if (optimalMax != null) allVals.push(optimalMax);
  const yMin = Math.min(...allVals);
  const yMax = Math.max(...allVals);
  const yPad = ((yMax - yMin) * 0.1) || Math.max(Math.abs(yMax) * 0.05, 0.5);

  const datasets = [
    {
      label: 'Q1–Median', data: blueBox,
      backgroundColor: 'rgba(59,130,246,0.8)', borderColor: '#2563EB',
      borderWidth: 1, barPercentage: 0.55, categoryPercentage: 0.9, grouped: false, order: 2,
    },
    {
      label: 'Median–Q3', data: greenBox,
      backgroundColor: 'rgba(52,211,153,0.8)', borderColor: '#10B981',
      borderWidth: 1, barPercentage: 0.55, categoryPercentage: 0.9, grouped: false, order: 2,
    },
    {
      label: 'Lower whisker', data: whiskLo,
      backgroundColor: '#475569', borderWidth: 0,
      barPercentage: 0.03, categoryPercentage: 0.9, grouped: false, order: 3,
    },
    {
      label: 'Upper whisker', data: whiskHi,
      backgroundColor: '#475569', borderWidth: 0,
      barPercentage: 0.03, categoryPercentage: 0.9, grouped: false, order: 3,
    },
    {
      label: 'Outliers', type: 'scatter', data: outliersData,
      backgroundColor: semantic.warningSolid, borderColor: semantic.warning, borderWidth: 1.5,
      pointRadius: 4, pointHoverRadius: 6, order: 1,
    },
    ...(optimalMin != null ? [{
      label: 'Opt Min', type: 'line',
      data: Array(labels.length).fill(optimalMin),
      borderColor: 'rgba(22,163,74,0.55)', borderDash: [6, 4], pointRadius: 0, borderWidth: 1.5, fill: false, order: 0,
    }] : []),
    ...(optimalMax != null ? [{
      label: 'Opt Max', type: 'line',
      data: Array(labels.length).fill(optimalMax),
      borderColor: 'rgba(22,163,74,0.55)', borderDash: [6, 4], pointRadius: 0, borderWidth: 1.5, fill: false, order: 0,
    }] : []),
  ];

  const sc = scaleCfg(c, boxes.length);
  const opts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    layout: { padding: { left: 12, right: 12 } },
    interaction: { mode: 'nearest', intersect: true },
    plugins: {
      legend: legendCfg(c),
      tooltip: {
        filter: (item) => item.dataset.label === 'Outliers' || item.datasetIndex === 0,
        callbacks: {
          title: (items) => {
            const first = items[0];
            if (!first) return '';
            if (first.dataset.label === 'Outliers') {
              return first.raw.x;
            }
            return labels[first.dataIndex] ?? '';
          },
          label: (item) => {
            if (item.dataset.label === 'Outliers') {
              return `Outlier: ${fmt2(item.raw.y)}`;
            }
            const b = boxes[item.dataIndex];
            if (!b) return '';
            return [
              `Max: ${fmt2(b.upperWhisker)}`,
              `Q3: ${fmt2(b.q3)}`,
              `Median: ${fmt2(b.median)}`,
              `Q1: ${fmt2(b.q1)}`,
              `Min: ${fmt2(b.lowerWhisker)}`,
            ];
          },
        },
      },
      zoom: ZOOM_CFG,
    },
    scales: {
      ...sc,
      x: {
        ...sc.x,
        offset: true,
        ticks: { ...sc.x.ticks, maxRotation: 45, autoSkip: true, maxTicksLimit: 14 },
        grid: { display: false },
      },
      y: { ...sc.y, min: +(yMin - yPad).toFixed(2), max: +(yMax + yPad).toFixed(2), beginAtZero: false },
    },
  };

  return (
    <div style={{ position: 'relative', height: stretch ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: stretch ? 'calc(100% - 28px)' : CHART_H_WIDE, position: 'relative', flex: stretch ? 1 : 'none', minHeight: 0 }}>
        <Bar ref={ref} data={{ labels, datasets }} options={opts} />
        <ResetBtn chartRef={ref} c={c} />
      </div>
    </div>
  );
}


// ── KDE Chart ─────────────────────────────────────────────────────────────────
// Ported from jspl_digital_twin reference:
// - kdeZoneFills interpolates boundary points and returns two fill datasets

// ── Correlation Scatter ───────────────────────────────────────────────────────
function CorrelationChart({ corr, paramName, paramUnit, c, stretch }) {
  const ref = useRef(null);
  if (!corr?.points?.length) return <Empty c={c} h={stretch ? '100%' : CHART_H} />;
  const { points, rValue, targetName, targetUnit } = corr;

  const n     = points.length;
  const sumX  = points.reduce((s, p) => s + p.x, 0);
  const sumY  = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX) || 0;
  const intcp = (sumY - slope * sumX) / n;
  const xMin  = Math.min(...points.map((p) => p.x));
  const xMax  = Math.max(...points.map((p) => p.x));

  const good = points.filter((p) =>  p.inOptimal);
  const bad  = points.filter((p) => !p.inOptimal);

  const datasets = [
    {
      label: 'In optimal',
      data: good.map((p) => ({ x: p.x, y: p.y })),
      backgroundColor: 'rgba(22,163,74,0.75)', pointRadius: 4, pointHoverRadius: 6,
    },
    {
      label: 'Out of optimal',
      data: bad.map((p) => ({ x: p.x, y: p.y })),
      backgroundColor: 'rgba(220,38,38,0.7)', pointRadius: 4, pointHoverRadius: 6,
    },
    {
      label: `Trend (r = ${rValue != null ? rValue.toFixed(2) : '—'})`,
      data: [{ x: xMin, y: slope * xMin + intcp }, { x: xMax, y: slope * xMax + intcp }],
      type: 'line',
      borderColor: 'rgba(100,116,139,0.65)', borderWidth: 1.5, borderDash: [6, 4],
      pointRadius: 0, fill: false, tension: 0,
    },
  ];

  const opts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    layout: { padding: { left: 4 } },
    plugins: {
      legend: legendCfg(c),
      tooltip: {
        callbacks: {
          label: (ctx) => ctx.datasetIndex > 1 ? '' : ` (${fmt2(ctx.parsed.x)} ${paramUnit ?? ''}, ${fmt2(ctx.parsed.y)} ${targetUnit ?? ''})`,
        },
      },
      zoom: ZOOM_CFG,
    },
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: `${paramName}${paramUnit ? ` (${paramUnit})` : ''}`, color: c.textSubtle, font: { size: 9 } },
        ticks: { color: c.textSubtle, font: { size: 9 } },
        grid: { color: c.border },
      },
      y: {
        title: { display: true, text: `${targetName}${targetUnit ? ` (${targetUnit})` : ''}`, color: c.textSubtle, font: { size: 9 } },
        ticks: { color: c.textSubtle, font: { size: 9 } },
        grid: { color: c.border },
      },
    },
  };

  return (
    <div style={{ height: stretch ? '100%' : CHART_H, position: 'relative' }}>
      <Scatter ref={ref} data={{ datasets }} options={opts} />
      <ResetBtn chartRef={ref} c={c} />
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'trends',       label: 'Parameter Trends' },
  { key: 'correlations', label: 'Parameter Correlation with Targets' },
];

export function ParameterChartsModal({ depId, param, allParams: _allParams = [], initialRangeDays = 7, onClose }) {
  const c      = useThemeColors();
  const isDark = useIsDark();

  const initialKey = DATE_RANGES.find((r) => r.days === initialRangeDays)?.key ?? '7d';
  const [rangeKey, setRangeKey] = useState(initialKey);
  const [activeTab, setActiveTab] = useState('trends');
  const [open, setOpen] = useState(true);
  const rangeDays = DATE_RANGES.find((r) => r.key === rangeKey)?.days ?? 7;
  const { from, to } = useMemo(() => rangeToISO(rangeDays), [rangeDays]);

  const { data, isLoading } = useParameterCharts(depId, param?.id, from, to);

  const handleClose = () => setOpen(false);

  const tabBtnStyle = (active) => ({
    padding: '7px 16px', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: active ? 600 : 500,
    fontFamily: typography.fontFamily.body,
    background: 'transparent',
    color: active ? c.textActive : c.textSubtle,
    borderBottom: active ? `2px solid ${c.textActive}` : '2px solid transparent',
    transition: 'all 0.15s',
  });

  return (
    <SlideModal isOpen={open} onClose={handleClose} onExited={onClose} maxWidth={1060} maxHeight="92vh" borderRadius={18} zIndex={1200} timeout={250} padding={20} fullHeight c={c} isDark={isDark}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '16px 20px 0', borderBottom: `1px solid ${c.border}`, flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: '0 0 2px', fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading, letterSpacing: typography.letterSpacing.normal }}>
              {param?.displayName}
            </h2>
            <p style={{ margin: '0 0 10px', fontSize: typography.fontSize.sm, color: c.textSubtle, fontFamily: typography.fontFamily.mono }}>{param?.dataKey}</p>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0 }}>
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setActiveTab(t.key)} style={tabBtnStyle(activeTab === t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '10px' }}>
            <div style={{
              display: 'inline-flex', borderRadius: radius.lg,
              border: `1px solid ${c.border}`,
              background: isDark ? 'rgba(255,255,255,0.04)' : c.bgSubtle,
              padding: '3px', gap: '2px',
            }}>
              {DATE_RANGES.map((r) => {
                const active = rangeKey === r.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => setRangeKey(r.key)}
                    style={{
                      padding: '4px 10px', borderRadius: radius.md, border: 'none', cursor: 'pointer',
                      fontSize: typography.fontSize.base, fontWeight: active ? 600 : 500,
                      fontFamily: typography.fontFamily.body,
                      background: active ? (isDark ? 'rgba(255,255,255,0.1)' : '#fff') : 'transparent',
                      color: active ? c.textActive : c.textSubtle,
                      boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textSubtle, padding: 4 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>close</span>
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: c.textSubtle, fontSize: typography.fontSize.lg }}>
              Loading charts…
            </div>
          ) : !data ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: c.textSubtle, fontSize: typography.fontSize.lg }}>
              No chart data available.
            </div>
          ) : activeTab === 'trends' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', flex: 1, minHeight: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <SectionTitle label="Box Plot — Day-wise Distribution" c={c} />
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  <BoxPlotChart data={data.boxPlot} c={c} isDark={isDark} stretch />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <SectionTitle label="Run Chart — Granular" c={c} />
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  <RunChart data={data.runChart} c={c} isDark={isDark} stretch />
                </div>
              </div>
            </div>
          ) : (
            /* Correlations tab */
            !data.correlations?.length ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: c.textSubtle, fontSize: typography.fontSize.lg }}>
                No target parameters found for correlation.
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
                gridAutoRows: '1fr',
                gap: '24px',
                minHeight: '100%',
              }}>
                {data.correlations.map((corr) => (
                  <div key={corr.targetId} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <SectionTitle
                      label={`vs ${corr.targetName}${corr.rValue != null ? `  ·  r = ${corr.rValue.toFixed(2)}` : ''}`}
                      c={c}
                    />
                    <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                      <CorrelationChart
                        corr={corr}
                        paramName={param?.displayName}
                        paramUnit={param?.unit}
                        c={c}
                        stretch
                      />
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </SlideModal>
  );
}
